import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../../../services/api-base';
import type {
  PromoCode,
  PromoCodeCreator,
  PromoListParams,
  PromoPage,
  PromoRequestPayload,
  UsageListParams,
  UsagePage,
} from '../model/PromoCodeModel';

const BASE = '/order-service/api/v1/orders/promocodes';

interface UpdatePromoArgs {
  id: string;
  body: PromoRequestPayload;
}

interface CheckCodeArgs {
  code: string;
  excludeId?: string;
}

/**
 * Spring Boot's `@RequestParam List<String>` expects repeated keys
 * (`createdByUserId=a&createdByUserId=b`); fetch's URLSearchParams default
 * isn't acceptable to Tomcat which rejects bracketed array params per RFC 7230.
 * The same constraint motivated the legacy axios `paramsSerializer`.
 */
const buildListQueryString = (p: PromoListParams): string => {
  const parts: string[] = [];
  const add = (key: string, value: string | number) =>
    parts.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );

  if (p.page != null) add('page', p.page);
  if (p.size != null) add('size', p.size);
  if (p.sort) add('sort', p.sort);
  if (p.search) add('search', p.search);
  if (p.status && p.status !== 'All') add('status', p.status);
  if (p.discountType && p.discountType !== 'All') add('discountType', p.discountType);
  p.createdByUserId?.forEach((v) => add('createdByUserId', v));

  return parts.join('&');
};

export const promoCodesApi = createApi({
  reducerPath: 'promoCodesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['PromoCodes', 'PromoCreators', 'PromoCodeUsage'],
  endpoints: (builder) => ({
    getPromoCodes: builder.query<PromoPage, PromoListParams>({
      query: (params) => `${BASE}?${buildListQueryString(params)}`,
      providesTags: ['PromoCodes'],
    }),

    getPromoCode: builder.query<PromoCode, string>({
      query: (id) => `${BASE}/${id}`,
    }),

    getPromoCreators: builder.query<PromoCodeCreator[], void>({
      query: () => `${BASE}/creators`,
      providesTags: ['PromoCreators'],
    }),

    /**
     * Used by the create/edit form to validate uniqueness as the user types.
     * Marked as a query (not mutation) so the result caches; the form's
     * sequence-tracking ref ensures stale resolutions don't clobber state.
     */
    checkPromoCodeAvailable: builder.query<
      { available: boolean },
      CheckCodeArgs
    >({
      query: ({ code, excludeId }) => {
        const params = new URLSearchParams({ code });
        if (excludeId) params.append('excludeId', excludeId);
        return `${BASE}/check-code?${params.toString()}`;
      },
    }),

    createPromoCode: builder.mutation<PromoCode, PromoRequestPayload>({
      query: (body) => ({
        url: BASE,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PromoCodes', 'PromoCreators', 'PromoCodeUsage'],
    }),

    updatePromoCode: builder.mutation<PromoCode, UpdatePromoArgs>({
      query: ({ id, body }) => ({
        url: `${BASE}/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PromoCodes', 'PromoCreators', 'PromoCodeUsage'],
    }),

    deactivatePromoCode: builder.mutation<PromoCode, string>({
      query: (id) => ({
        url: `${BASE}/${id}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['PromoCodes', 'PromoCodeUsage'],
    }),

    reactivatePromoCode: builder.mutation<PromoCode, string>({
      query: (id) => ({
        url: `${BASE}/${id}/reactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['PromoCodes', 'PromoCodeUsage'],
    }),

    getPromoCodeUsage: builder.query<UsagePage, UsageListParams>({
      query: ({ promocodeId, page = 0, size = 25, sort, search }) => {
        const queryString = buildListQueryString({
          page,
          size,
          sort,
          search: search?.trim() || undefined,
        });
        return `${BASE}/${promocodeId}/usage?${queryString}`;
      },
      providesTags: ['PromoCodeUsage'],
    }),
  }),
});

export const {
  useGetPromoCodesQuery,
  useLazyGetPromoCodesQuery,
  useGetPromoCodeQuery,
  useLazyGetPromoCodeQuery,
  useGetPromoCreatorsQuery,
  useLazyGetPromoCreatorsQuery,
  useLazyCheckPromoCodeAvailableQuery,
  useCreatePromoCodeMutation,
  useUpdatePromoCodeMutation,
  useDeactivatePromoCodeMutation,
  useReactivatePromoCodeMutation,
  useGetPromoCodeUsageQuery,
} = promoCodesApi;
